import {
  type GlobalTabType,
  type IGlobalTab,
  type TabGroupName,
  TabType,
} from '@carboncopyco/types/desktop';
import {
  SettingMainItemsKeys,
  SettingsSubKeys,
} from '@carboncopyco/types/desktop/appSettings';
import { Button, TooltipBubble } from '@carboncopyco/ui';
import { type ReactNode, useEffect, useRef } from 'react';
import { settings } from '../../../../../settings';
import { settingsState } from '../../../../shared/appSettings';
import { BROWSER_WINDOW_HOME_PAGE } from '../../../../shared/constants/constants';
import { UserEventsEnum } from '../../../../shared/enums/UserEventsEnum';
import { useFlagsFallback } from '../../../hooks/useFlagsFallback';
import useOverflowedElements from '../../../hooks/useOverflowedElements';
import { useTabManager } from '../../../services/TabManager';
import MoreTabsDropdown from './MoreTabsDropdown';
import { OverflowedElementsShadow } from './OverflowedElementsShadow';
import { HomeTab, Tab } from './items';
import { type DefaultThemeType, baseTheme, getCSSProperties } from './theme';

interface AppTabsProps {
  onTabClick: (tabId: number) => void;
  onTabClose: (tabId: number) => void;
  activeTabId: number;
  theme?: DefaultThemeType;
  children: ReactNode;
  showDropdownMenu?: boolean;
  homeTabClassName?: string;
  startExtraContent?: ReactNode;
  endExtraContent?: ReactNode;
  moreTabsDropdownIcon?: ReactNode;
  moreTabsOpenOnClick?: boolean;
  showAddNewBrowserTabButton: boolean;
}

const getTitleAdditionalText = (globalTabs: IGlobalTab[], tab: IGlobalTab) => {
  const hiddenTabsIdsByGroupId = globalTabs.reduce(
    (acc, globalTab) => {
      if (!globalTab.group || !globalTab.hidden) return acc;
      if (!acc[globalTab.group.id]) acc[globalTab.group.id] = [];
      acc[globalTab.group.id].push(tab.id);
      return acc;
    },
    {} as Record<number, number[]>
  );

  const isExternalUrlAsset =
    tab.type === TabType.BROWSER_WINDOW && tab.group?.mergingOptions?.allowed;

  const visibleIfGroupIsInactiveCount = 1;

  return ([
    TabType.GROUP_DEMO_PREVIEW,
    TabType.GROUP_DEMO_PRESENTATION,
    TabType.GROUP_TOUR_PRESENTATION,
    TabType.GROUP_TOUR_PREVIEW,
  ].includes(tab.type) ||
    isExternalUrlAsset) &&
    tab.group?.mergingOptions &&
    !tab.group.mergingOptions.isDetached
    ? ` (${(hiddenTabsIdsByGroupId[tab.group.id]?.length || 0) + visibleIfGroupIsInactiveCount})`
    : '';
};

const AppTabs = ({
  activeTabId,
  onTabClick,
  onTabClose,
  theme = baseTheme,
  startExtraContent,
  endExtraContent,
  moreTabsDropdownIcon,
  moreTabsOpenOnClick,
  showDropdownMenu,
  homeTabClassName,
  children,
  showAddNewBrowserTabButton,
}: AppTabsProps) => {
  const { createGlobalTab, getGlobalTabById, globalTabs } = useTabManager();
  const tabsWrapperRef = useRef<HTMLUListElement>(null);
  const { browserMode } = useFlagsFallback();

  const activeTabData = getGlobalTabById(activeTabId);
  const openBrowserWindow = async (url?: string) => {
    const storeAddress = await settingsState.getItem(
      SettingMainItemsKeys.BrowserSettings,
      SettingsSubKeys.BrowserAddress
    );
    createGlobalTab<GlobalTabType>({
      type: TabType.BROWSER_WINDOW,
      title: 'Untitled',
      url: url || storeAddress || BROWSER_WINDOW_HOME_PAGE || settings.WEB_APP,
      payload: activeTabData?.payload,
      ...(activeTabData.group
        ? {
            group: {
              id: activeTabData.group.id,
              name: activeTabData.group.name as TabGroupName,
              isVisibleIfGroupIsActive: true,
              isVisibleIfGroupIsInactive: false,
              showNonRelatedGroupTabsIfActive: false,
              focusOnCreate: true,
              closeGroupOnClose: false,
            },
          }
        : {}),
      createGlobalTabOnElectron: {
        startOn: 'tab-creation',
        withInterceptor: false,
        withWebSecurity: true,
      },
    });
  };

  useEffect(() => {
    window.dsDesktop.ipcRenderer.on(
      UserEventsEnum.MAIN_TO_RENDERER_OPEN_NEW_BROWSER_TAB,
      (url: any) => openBrowserWindow(url?.toString() || '')
    );
  }, [globalTabs]);

  const { hasOverflowedElements } = useOverflowedElements(tabsWrapperRef, [
    globalTabs.length,
    activeTabId,
    showDropdownMenu,
  ]);

  const showMoreTabsDropdown = hasOverflowedElements || showDropdownMenu;

  return (
    <div
      className="flex flex-col flex-grow flex-shrink basis-0 min-w-0"
      style={getCSSProperties(theme)}
    >
      <div className="flex relative select-none transition-colors bg-[var(--ds-tab-inactive-bg)]">
        {startExtraContent && (
          <div className="flex shrink-0 rtl:order-1">{startExtraContent}</div>
        )}

        <div className="flex flex-shrink min-w-0 basis-auto relative">
          <div
            className={`absolute inset-y-0 start-1 ${
              globalTabs.length > 1 ? '-end-px' : 'end-0'
            } bg-[var(--ds-tab-common-divider-color)]`}
          />
          <ul
            className="flex flex-shrink min-w-0 basis-auto overflow-hidden gap-px relative"
            role="tablist"
            ref={tabsWrapperRef}
          >
            <li className="flex shrink-0">
              <TooltipBubble
                title="Home"
                placement="auto-end"
              >
                <HomeTab
                  tab={globalTabs[0]}
                  activeTabId={activeTabId}
                  theme={theme}
                  onClick={onTabClick}
                  className={homeTabClassName}
                />
              </TooltipBubble>
            </li>
            {globalTabs.map((tab, index) => {
              const isHomeTab = index === 0;
              if (isHomeTab || tab.hidden) return null;
              return (
                <li
                  className="flex min-w-[78px]"
                  key={tab.id}
                >
                  <Tab
                    tab={tab}
                    activeTabId={activeTabId}
                    theme={theme}
                    onClick={onTabClick}
                    onClose={onTabClose}
                    className="border-transparent border-x-solid border-x"
                    titleAdditionalText={getTitleAdditionalText(
                      globalTabs,
                      tab
                    )}
                  />
                </li>
              );
            })}
          </ul>
          {hasOverflowedElements && <OverflowedElementsShadow />}
        </div>
        {showAddNewBrowserTabButton && browserMode && (
          <TooltipBubble
            width={36}
            title="New tab"
            placement="bottom"
            offset={[0, 5]}
          >
            <Button
              onClick={() => openBrowserWindow()}
              className="relative ms-px shrink-0 w-9 h-10 text-ds-natural leading-none pb-0.5 flex my-auto justify-center items-center bg-[--ds-tab-inactive-bg] hover:bg-[--ds-tab-inactive-hover-bg] hover:text-white transition-colors text-lg"
              data-testid="open-browser-window-button"
            >
              +
            </Button>
          </TooltipBubble>
        )}

        {/* Draggable area */}
        <div className="flex-grow draggable" />

        {/* Dropdown */}
        <div className="flex justify-center w-[40px] shrink-0">
          {showMoreTabsDropdown ? (
            <MoreTabsDropdown
              tabs={globalTabs}
              activeTabId={activeTabId}
              onTabClick={onTabClick}
              theme={theme}
              dropdownIcon={moreTabsDropdownIcon}
              openOnClick={moreTabsOpenOnClick}
            />
          ) : (
            <div className="flex-grow h-full draggable" />
          )}
        </div>

        {endExtraContent && (
          <div className="flex shrink-0 rtl:-order-1">{endExtraContent}</div>
        )}
      </div>

      {/* Tabs content */}
      <div className="flex flex-col flex-grow flex-shrink basis-0 min-w-0">
        {children}
      </div>
    </div>
  );
};

export default AppTabs;
